import { Form, Formik, FormikProps } from 'formik';
import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { ParameterGroupDto } from 'src/store/parametergroups/parametergroup';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  parameterGroup: Nullable<ParameterGroupDto>;
  onCancel: () => void;
  onSubmit: (parameterGroup: ParameterGroupDto) => void;
};

export const ParameterGroupForm = forwardRef<
  FormikProps<ParameterGroupDto>,
  Props
>((props, ref) => {
  const { onSubmit, onCancel } = props;

  const [parameterGroup] = useState<ParameterGroupDto>(
    props.parameterGroup || ({} as ParameterGroupDto)
  );
  const { t } = useTranslation();

  const validate = (parameterGroup: ParameterGroupDto) => {
    const validationRunners = [
      validationRunner('no', t('props.number'), required),
      validationRunner('name', t('props.name'), required, maxLength(50)),
      validationRunner('description', t('props.description'), maxLength(200))
    ];
    return runValidations(parameterGroup, validationRunners);
  };

  return (
    <Formik
      initialValues={parameterGroup}
      validate={validate}
      innerRef={ref}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        submitCount
      }) => (
        <Form onSubmit={handleSubmit}>
          <InputField
            name="no"
            type="number"
            label={t('props.number')}
            value={values.no}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.no}
            displayError={touched.no || submitCount > 0}
          />
          <InputField
            name="name"
            label={t('props.name')}
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.name}
            displayError={touched.name || submitCount > 0}
          />
          <InputField
            name="description"
            label={t('props.description')}
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.description}
            displayError={touched.description || submitCount > 0}
          />

          <ModalFooter>
            <TertiaryButton
              onClick={onCancel}
              label={t('cancel')}
              className="mr-32"
            />
            <PrimaryButton
              type="submit"
              label={t('save.verb')}
              disabled={isSubmitting}
            />
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
});
